import styled from "styled-components";
import { format } from "date-fns";
import { FaTrash } from "react-icons/fa6";
import { MdEdit } from "react-icons/md";
import { CashMovement } from "../../../types/common.types";
import { mobile } from "../../../styles/responsive";
import { useAdmin } from "../../../context/admin";
import { useGlobal } from "../../../context/global";
import AdminService from "../../../services/admin.service";
import removeTimezone from "../../../utils/removeTimezone";

export default function List({ list, onChanged }: { list: CashMovement[], onChanged?: () => void }) {
    const { setEditCashMovementData, setConfirmDeleteCashMovementId } = useAdmin()
    const { setIsLoading } = useGlobal()

    // Una venta "Sin asignar" que nunca va a tener un pasajero que asignarle (ej. se devolvió el
    // dinero antes de vincularla) — se marca como revisada con un motivo corto, sin necesidad de
    // un payment_reservation, así deja de aparecer en la cola de revisión semanal.
    async function resolveUnassigned(item: CashMovement) {
        const note = window.prompt('Motivo (ej. "reembolsado", "duplicado"):', item.unassigned_resolved_note || '')
        if (note === null) return // canceló
        setIsLoading(true)
        try {
            await AdminService.resolveUnassignedVenta(item.id, true, note)
            onChanged?.()
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    async function unresolveUnassigned(item: CashMovement) {
        setIsLoading(true)
        try {
            await AdminService.resolveUnassignedVenta(item.id, false)
            onChanged?.()
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Main>
            {list.length === 0 && <p className="empty">Sin movimientos en el período seleccionado</p>}
            {list.map((item) => {
                // Una venta asignada a un pago pendiente (el pago semanal de un aliado) ya está asignada,
                // aunque no apunte a un pago de pasajero puntual.
                const isUnassignedVenta = item.movement_type === 'ingreso' && item.category?.name?.startsWith('Ventas') && !item.payment_reservation_id && !item.pending_payment_id
                return (
                <div className={`row ${item.movement_type}`} key={item.id}>
                    <div className="main">
                        <span className="amount">
                            {item.movement_type === 'ingreso' ? '+' : '-'} ${Number(item.amount).toLocaleString('es-CL')}
                        </span>
                        <span className="category">
                            {item.category?.name}
                            {isUnassignedVenta && !item.unassigned_resolved && (
                                <span className="unassigned-badge">Sin asignar</span>
                            )}
                            {isUnassignedVenta && item.unassigned_resolved && (
                                <span className="resolved-badge" title={item.unassigned_resolved_note || ''}>Revisado</span>
                            )}
                        </span>
                        <span className="account">{item.account?.name}</span>
                    </div>
                    <div className="detail">
                        {item.counterpart_name && <span className="counterpart">{item.counterpart_name}</span>}
                        {item.detail && <span className="text">{item.detail}</span>}
                        <span className="date">
                            {item.execution_date ? removeTimezone(item.execution_date) : format(new Date(item.created_at), 'dd/MM/yyyy')}
                            {' · registrado ' + format(new Date(item.created_at), 'dd/MM H:mm')}
                        </span>
                    </div>
                    <div className="actions">
                        {item.photo && typeof item.photo === 'string' &&
                            <a href={`${process.env.REACT_APP_API_URL}/files/${item.photo}`} target="_blank" rel="noreferrer">Foto</a>
                        }
                        {isUnassignedVenta && !item.unassigned_resolved && (
                            <button type="button" className="resolve-btn" onClick={() => resolveUnassigned(item)}>Marcar como revisado</button>
                        )}
                        {isUnassignedVenta && item.unassigned_resolved && (
                            <button type="button" className="resolve-btn" onClick={() => unresolveUnassigned(item)}>Deshacer revisión</button>
                        )}
                        {!item.auto_generated && (
                            <>
                                <button type="button" onClick={() => setEditCashMovementData(item)}><MdEdit/></button>
                                <button type="button" onClick={() => setConfirmDeleteCashMovementId(item.id)}><FaTrash/></button>
                            </>
                        )}
                    </div>
                </div>
                )
            })}
        </Main>
    )
}

const Main = styled.div`
display: flex;
flex-direction: column;
gap: 10px;
.empty {
    text-align: center;
    color: ${props => props.theme.gray};
    padding: 20px 0;
}
.row {
    display: grid;
    grid-template-columns: 1fr 2fr auto;
    align-items: center;
    gap: 10px;
    padding: 12px 15px;
    border-radius: 10px;
    background: ${props => props.theme.whiteBackground};
    border-left: 5px solid ${props => props.theme.gray};
    ${mobile({
        gridTemplateColumns: '1fr',
        gap: '5px'
    })}
}
.row.ingreso {
    border-left-color: ${props => props.theme.blue};
}
.row.egreso {
    border-left-color: ${props => props.theme.lightRed};
}
.main {
    display: flex;
    flex-direction: column;
    gap: 3px;
    .amount {
        font-size: 18px;
        font-weight: 700;
    }
    .category {
        font-size: 13px;
        color: ${props => props.theme.gray};
    }
    .unassigned-badge {
        margin-left: 6px;
        padding: 1px 6px;
        border-radius: 4px;
        background-color: ${props => props.theme.yellowDark};
        color: ${props => props.theme.white};
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
    }
    .resolved-badge {
        margin-left: 6px;
        padding: 1px 6px;
        border-radius: 4px;
        background-color: ${props => props.theme.gray};
        color: ${props => props.theme.white};
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        cursor: help;
    }
    .account {
        font-size: 12px;
        color: ${props => props.theme.gray};
    }
}
.detail {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 13px;
    .counterpart {
        font-weight: 600;
    }
    .date {
        color: ${props => props.theme.gray};
        font-size: 12px;
    }
}
.actions {
    display: flex;
    align-items: center;
    gap: 8px;
    a {
        font-size: 13px;
        color: ${props => props.theme.blue};
    }
    button {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: none;
        background-color: ${props => props.theme.whiteBone};
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .resolve-btn {
        width: auto;
        height: auto;
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
    }
}
`
